const formatQuantity = (value) => {
  if (value == null || isNaN(value)) return 0;
  const num = parseFloat(value);
  return Math.round(num * 100) / 100;
};

// Count-noun units that have proper plural forms in i18next via _one/_few/_many/_other.
// Their plural variants (e.g. "pieces", "slices") are normalized to their singular base.
const COUNT_NOUN_BASE_MAP = {
  piece: "piece",
  pieces: "piece",
  slice: "slice",
  slices: "slice",
  head: "head",
  heads: "head",
};

// Part-to-whole products whose `wholeUnits` include "head" — for these the
// generic "piece/unit" display should be replaced with the head-class plural
// (cabeza/cabezas, head/heads, головка/головки/головок, ...).
// Keep this list in sync with PART_TO_WHOLE_CONVERSIONS in groceryUtilsMetric.js.
const HEAD_CLASS_PRODUCT_PATTERNS = [
  "garlic",
  "lettuce",
  "romaine",
  "iceberg",
  "butterhead",
  "little gem",
  "escarole",
  "cabbage",
  "red cabbage",
  "savoy cabbage",
  "napa cabbage",
];

const isHeadClassProduct = (item) => {
  const candidates = [item?.name, item?.productName, item?.originalName]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());
  if (candidates.length === 0) return false;
  return HEAD_CLASS_PRODUCT_PATTERNS.some((pattern) =>
    candidates.some((c) => c.includes(pattern))
  );
};

const HEAD_DISPLAY_UNIT_INPUTS = new Set([
  "piece",
  "pieces",
  "unit",
  "units",
  "ud",
  "uds",
  "pcs",
  "head",
  "heads",
]);

// When a head-class product is rendered with a generic count unit, swap the
// display code to "head" so localizeUnit picks the head-class plural.
const resolveDisplayUnitCode = (item, unit) => {
  if (!unit) return unit;
  const code = String(unit).toLowerCase().trim();
  if (HEAD_DISPLAY_UNIT_INPUTS.has(code) && isHeadClassProduct(item)) {
    return "head";
  }
  return unit;
};

// Localize a base weight/volume/count unit code (g/kg/ml/l/oz/lb/pcs/piece/slice/...).
// When `count` is provided AND the unit is a count noun, i18next picks the right plural form.
// Falls back to the raw code when the i18n key is missing.
const localizeUnit = (t, code, count) => {
  if (!code) return "";
  const key = String(code).toLowerCase().trim();
  const base = COUNT_NOUN_BASE_MAP[key];
  if (base && typeof count === "number") {
    return t(`grocery.units.${base}`, { count, defaultValue: code });
  }
  return t(`grocery.units.${key}`, { defaultValue: code });
};

const getDisplayUnit = (t, unit, count) => localizeUnit(t, unit, count);

// Build a localized "1 medium (~241g) + 1 small (~181g)" string from the
// structured unitBreakdown produced by the grocery processor.
const formatUnitBreakdown = (t, breakdown) => {
  if (!Array.isArray(breakdown) || breakdown.length === 0) return null;
  return breakdown
    .filter((part) => part && part.count > 0)
    .map((part) => {
      const sizeWord = t(`grocery.unitSizes.${part.size}`, {
        defaultValue: part.size,
      });
      const unitWord = localizeUnit(t, part.weightUnit || "g");
      return `${part.count} ${sizeWord} (~${part.weightPerUnit}${unitWord})`;
    })
    .join(" + ");
};

// Legacy fallback: parse the English `unitInfo` string produced for
// previously-saved meal plans (no `unitBreakdown` field) and translate
// the size words and units in place.
// Examples handled:
//   "1 medium (~241g each)"
//   "1 large (~300g each) + 2 small (~180g each)"
//   "1 medium (~8.5oz each)"
const formatLegacyUnitInfo = (t, unitInfo) => {
  if (!unitInfo || typeof unitInfo !== "string") return null;
  const partRegex = /(\d+)\s+(large|medium|small)\s*\(~([\d.]+)\s*(g|kg|ml|l|oz|lb)\s+each\)/gi;
  const parts = [];
  let match;
  while ((match = partRegex.exec(unitInfo)) !== null) {
    const [, count, size, weight, weightUnit] = match;
    const sizeWord = t(`grocery.unitSizes.${size.toLowerCase()}`, {
      defaultValue: size,
    });
    const unitWord = localizeUnit(t, weightUnit);
    parts.push(`${count} ${sizeWord} (~${weight}${unitWord})`);
  }
  return parts.length > 0 ? parts.join(" + ") : unitInfo;
};

export const calculateDisplayDetails = ({
  item,
  t,
  measurementSystem,
  currencySymbol,
  formatMoney,
  translatePackageDescription,
}) => {
  const requiredQty = formatQuantity(item.totalQty || 0);
  const unit = item.unit || item.measurementUnit || t("grocery.unit");

  // Base unit codes for current measurement system
  const weightUnitCode = measurementSystem === "metric" ? "g" : "oz";
  const heavyUnitCode = measurementSystem === "metric" ? "kg" : "lb";
  const weightUnitWord = localizeUnit(t, weightUnitCode);
  const heavyUnitWord = localizeUnit(t, heavyUnitCode);
  const pcsWord = localizeUnit(t, "pcs");

  // Prefer structured breakdown (locale-aware); fall back to regex-translated legacy string.
  const localizedBreakdown =
    formatUnitBreakdown(t, item.unitBreakdown) ||
    formatLegacyUnitInfo(t, item.unitInfo) ||
    null;

  if (item.unmatched === true || item.error) {
    return {
      displayStr: `${requiredQty} ${getDisplayUnit(t, unit, Math.round(requiredQty))}`,
      toBuyStr: `${item.error || t("grocery.productNotAvailable")} • ${t(
        "grocery.noPriceAvailable"
      )}`,
      isError: true,
    };
  }

  if (item.ai_data) {
    const rawDescription = item.selectedSize?.description || item.description || "";
    const description = translatePackageDescription(rawDescription);
    return {
      displayStr: `${requiredQty} ${getDisplayUnit(t, unit, Math.round(requiredQty))}`,
      toBuyStr: `${formatMoney(item.totalPrice || 0)}${
        description ? ` • ${description}` : ""
      }`,
      isAI: true,
    };
  }

  if (item.type === "weight") {
    const avgWeight = formatQuantity(
      item.selectedSize?.avgWeight || item.avgWeight?.metric || item.avgWeight?.imperial || 0
    );
    const rawPrice = item.selectedSize?.price || item.pricePerWeight || 0;
    const rawWeightUnit = item.selectedSize?.weightUnit || item.measurementUnit || "g";
    let productWeight = item.selectedSize?.weight || 1000;

    if (productWeight < 50 && rawWeightUnit === "g") {
      if (productWeight < 10 && productWeight > 0) {
        productWeight = productWeight * 1000;
        console.warn(
          `⚠️ ProductList: Corrected small weight ${item.selectedSize?.weight}g to ${productWeight}g for ${item.productName}`
        );
      } else {
        productWeight = 1000;
        console.warn(
          `⚠️ ProductList: Using 1kg fallback for suspicious weight ${item.selectedSize?.weight}g for ${item.productName}`
        );
      }
    }

    let pricePerKg = rawPrice;
    if (rawWeightUnit === "g" && productWeight) {
      pricePerKg = (rawPrice / productWeight) * 1000;
    } else if (rawWeightUnit === "kg") {
      pricePerKg = rawPrice / (productWeight || 1);
    }

    const priceDisplay = `${currencySymbol}${pricePerKg.toFixed(2)}/${heavyUnitWord}`;

    if (item.unitInfo && item.smartSizing) {
      const displayWeight = `${requiredQty}${weightUnitWord}`;
      return {
        displayStr: `${displayWeight} (${localizedBreakdown})`,
        toBuyStr: `${formatMoney(item.totalPrice || 0)} • ${priceDisplay}`,
      };
    }

    if (avgWeight && requiredQty <= avgWeight) {
      const displayWeight = Math.round(avgWeight * 0.8);
      const price = (pricePerKg * displayWeight) / 1000;

      return {
        displayStr: `${requiredQty}${weightUnitWord} • ${t("grocery.pieceIs")} ${displayWeight}${weightUnitWord}`,
        toBuyStr: `${formatMoney(price)} • ${priceDisplay}`,
      };
    }

    if (avgWeight && requiredQty > avgWeight) {
      const unitsRequired = Math.ceil(requiredQty / avgWeight);
      const displayWeight = avgWeight * unitsRequired;
      const price = (pricePerKg * displayWeight) / 1000;

      return {
        displayStr: `${requiredQty}${weightUnitWord} • ${t("grocery.piecesIs", {
          count: unitsRequired,
        })} ${displayWeight}${weightUnitWord}`,
        toBuyStr: `${formatMoney(price)} • ${priceDisplay}`,
      };
    }

    return {
      displayStr: `${requiredQty}${weightUnitWord}`,
      toBuyStr: `${formatMoney(item.totalPrice || 0)} • ${priceDisplay}`,
    };
  }

  if (item.type === "package") {
    const rawDescription = item.selectedSize?.description || item.description || "";
    const packageDescription = translatePackageDescription(rawDescription);
    const itemsQty = item.selectedSize?.itemsQty;
    const packageWeight = item.selectedSize?.weight;
    const weightUnit = item.selectedSize?.weightUnit || "g";
    const displayUnitCode = resolveDisplayUnitCode(item, unit);

    let packageInfo = packageDescription;
    if (itemsQty && itemsQty > 1) {
      // For head-class products (garlic mesh bag of N heads), say "N cabezas"
      // instead of the generic "N pcs/uds" so the user understands what they buy.
      const itemsLabel =
        displayUnitCode === "head"
          ? localizeUnit(t, "head", itemsQty)
          : pcsWord;
      packageInfo = `${packageDescription} ${itemsQty} ${itemsLabel}`;
    } else if (packageWeight && packageWeight > 0) {
      const localizedWeightUnit = localizeUnit(t, weightUnit);
      // Show the description only when it maps to a known packaging type in our
      // i18n dictionary (bolsa, malla, botella, bandeja, tarro, pack, …).
      // Anything else — vendor format codes like "Pack-6", "Lote-3", future codes —
      // is suppressed because it conveys no useful information to the user.
      // When we reach this branch, itemsQty is already ≤ 1 (the branch above
      // handles itemsQty > 1), so no extra guard is needed.
      const rawNormalized = rawDescription.toLowerCase().trim();
      const isKnownPackageType = !!t(`grocery.packageTypes.${rawNormalized}`, { defaultValue: null });
      packageInfo = isKnownPackageType
        ? `${packageDescription} ${packageWeight}${localizedWeightUnit}`
        : `${packageWeight}${localizedWeightUnit}`;
    }

    let packagesNeeded = item.itemsQuantity || 1;

    if (!item.itemsQuantity && requiredQty > 0) {
      if (packageWeight && packageWeight > 0) {
        let packageWeightInBase = packageWeight;
        if (weightUnit === "kg") {
          packageWeightInBase = packageWeight * 1000;
        } else if (weightUnit === "l" || weightUnit === "L") {
          packageWeightInBase = packageWeight * 1000;
        }
        packagesNeeded = Math.ceil(requiredQty / packageWeightInBase);
      } else if (itemsQty && itemsQty > 0) {
        packagesNeeded = Math.ceil(requiredQty / itemsQty);
      }
    }

    const qtyLabel = packagesNeeded > 1 ? ` • ${t("grocery.qty")}: ${packagesNeeded}` : "";

    return {
      displayStr: `${requiredQty} ${getDisplayUnit(t, displayUnitCode, Math.round(requiredQty))}`,
      toBuyStr: `${formatMoney(item.totalPrice || 0)} • ${packageInfo}${qtyLabel}`,
    };
  }

  if (item.type === "unit") {
    const avgWeight = formatQuantity(
      item.selectedSize?.avgWeight || item.avgWeight?.metric || item.avgWeight?.imperial || 0
    );
    const pricePerUnit = item.selectedSize?.price || item.pricePerUnit || 0;
    const displayUnitCode = resolveDisplayUnitCode(item, unit);

    const weightInfo = avgWeight ? ` (~${avgWeight}${weightUnitWord})` : "";

    if (item.unitInfo && item.smartSizing) {
      return {
        displayStr: `${requiredQty} ${getDisplayUnit(t, displayUnitCode, Math.round(requiredQty))}${weightInfo} (${localizedBreakdown})`,
        toBuyStr: `${formatMoney(item.totalPrice || 0)} • ${formatMoney(pricePerUnit)}${t(
          "grocery.perUnit"
        )}`,
      };
    }

    return {
      displayStr: `${requiredQty} ${getDisplayUnit(t, displayUnitCode, Math.round(requiredQty))}${weightInfo}`,
      toBuyStr: `${formatMoney(item.totalPrice || 0)} • ${formatMoney(pricePerUnit)}${t(
        "grocery.perUnit"
      )}`,
    };
  }

  return {
    displayStr: `${requiredQty} ${getDisplayUnit(t, unit, Math.round(requiredQty))}`,
    toBuyStr: `${formatMoney(item.totalPrice || 0)}`,
  };
};
